import { useContext, useEffect, useRef, useState } from "react"
import { Toast } from "primereact/toast"
import { useLazyQuery } from "@apollo/client"
import { DateTime } from "luxon"
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

// Custom components
import { GET_SERVICES } from "./apptQueries"
import { UserContext } from "../../context/userContext"
import AppointmentsDialog from "./AppointmentsDialog"
import AppointmentsRequestDialog from "./AppointmentsRequestDialog"

// Utilities
import { LogError } from "../../helpers/logger"
import {
  declineParticipantAppointment,
  editParticipantAppointment,
  getAllMinifiedAppointments,
  getAvailabilities,
  sendParticipantAppointment,
} from "./AppointmentsApi"
import {
  formatDate,
  getDate,
  getLocalControllerDescription,
  reformatAppointmentRequest,
  reformatFormikPayload,
  requestModalModeOptions,
} from "./utils"
import { generateFutureRecurringEventsForControllers } from "./utilsRecurringControllers"
import { applyAppointmentFilters, filterServiceTemplates } from "./utilsFilters"
import { Badge } from "primereact/badge"

export default function Appointments() {
  const toast = useRef(null)
  const initializationCompleted = useRef(false) // Ref to track if initialization is complete.
  const userCtx = useContext(UserContext)
  const [showApptsDialog, setShowApptsDialog] = useState(false)
  const [services, setServices] = useState(null)
  const [appointments, setAppointments] = useState(null) // State to hold fetched (unfiltered) appointments.
  const [todayAppointmentsCount, setTodayAppointmentsCount] = useState(0) // State to hold today's appointments.
  const [filteredAppointments, setFilteredAppointments] = useState(null) // State to hold filtered appointments.
  const [loadingAppointments, setLoadingAppointments] = useState(true)
  const [loadingAppointmentsSmall, setLoadingAppointmentsSmall] =
    useState(false)
  const [loadingServices, setLoadingServices] = useState(true)
  const [filterServices, setFilterServices] = useState([])
  const [filterDateRange, setFilterDateRange] = useState([
    DateTime.local().startOf("month").toJSDate(),
    DateTime.local().endOf("month").toJSDate(),
  ])
  const [isCalendarView, setIsCalendarView] = useState(true) // State to toggle between calendar and list views.
  const [calSelectedDate, setCalSelectedDate] = useState(new Date()) // State to hold the selected date in calendar view.
  const [appointmentToEdit, setAppointmentToEdit] = useState(null) // State to hold the appointment being edited.

  // Request/edit dialog state
  const [showRequestAppt, setShowRequestAppt] = useState(false) // State to control visibility of the appointment request/edit dialog.
  const [requestModalMode, setRequestModalMode] = useState(
    requestModalModeOptions.request,
  ) // State to hold the mode of the appointment modal (e.g., "Request Appointment", "Edit Appointment").

  // GraphQL query to get services available for appointments.
  const [getServices] = useLazyQuery(GET_SERVICES, {
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    fetchScheduledAppointmentsToday() // Fetch today's appointments count on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Use effect to fetch services when the dialog is shown.
  useEffect(() => {
    if (!initializationCompleted.current && showApptsDialog) {
      const fetchData = async () => {
        await fetchServices()
        await fetchAppointments()
        initializationCompleted.current = true // Set the flag to true once services and appointments are fetched.
      }
      fetchData()
    } else if (!showApptsDialog) {
      setCalSelectedDate(DateTime.now().toJSDate()) // Reset selected date to today when dialog is closed.
      setFilterDateRange([
        DateTime.local().startOf("month").toJSDate(),
        DateTime.local().endOf("month").toJSDate(),
      ]) // Reset date range to current month when dialog is closed.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showApptsDialog])

  useEffect(() => {
    if (initializationCompleted.current && filterDateRange?.length === 2) {
      fetchAppointments(true) // Refetch appointments when the date range changes.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDateRange])

  useEffect(() => {
    if (initializationCompleted.current && appointments && services) {
      setFilteredAppointments(filterAppointments(appointments)) // Apply filters to the appointments when filterServices changes.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, services, filterServices])

  // Function to fetch services for the user's organizations.
  async function fetchServices() {
    try {
      setLoadingServices(true)

      // Get services for each orghanization the user is part of.
      const orgServices = await Promise.all(
        userCtx.participant_organizations.map(async (org) => {
          const serviceData = await getServices({
            variables: {
              organization_id: org.organization_id, // Use the current organization's ID.
              template_type: "session_module", // Filter by session module templates.
            },
          })

          return {
            organization_id: org.organization_id, // Include organization ID in the result.
            services: serviceData.data.getTemplatesByOrganization || [], // Extract services from the query result.
          }
        }),
      )

      // Process the service data as needed, e.g., set it in state or context.
      const _services = {}
      orgServices.forEach((org) => {
        _services[org.organization_id] = filterServiceTemplates(
          org.services,
        ).map((x) => x.template_title)
      })

      // Flatten all services into a single unique list
      _services.flat_unique = [...new Set(Object.values(_services).flat())]

      setServices(_services)
    } catch (error) {
      LogError("Error fetching services:", error)
      userCtx.setNotificationError(
        `Unable to load service types for appointments.`,
      )
      return
    } finally {
      setLoadingServices(false)
    }
  }

  // Function to fetch appointments, including scheduled, recurring, and requests.
  async function fetchAppointments(refetch = false) {
    try {
      refetch ? setLoadingAppointmentsSmall(true) : setLoadingAppointments(true)

      const _appointments = {
        upcoming: [],
        past: [],
        pending: [],
        declined: [],
        recurring: [],
      }

      // Run fetchScheduledAppointments first
      const _scheduledAppointments = await fetchScheduledAppointments()

      // Run the rest in parallel (except fetchRecurringAppointments)
      const [
        _recurringAppointments,
        _appointmentRequests,
        // _staffAvailabilities
      ] = await Promise.all([
        fetchRecurringAppointments(_scheduledAppointments.scheduled_all),
        fetchAppointmentRequests(),
        // fetchStaffAvailabilities()
      ])

      // Set past appointments.
      _appointments.past = _scheduledAppointments.past || []

      // Set upcoming and recurring appointments.
      _appointments.upcoming = _recurringAppointments.upcoming || []
      _appointments.recurring = _recurringAppointments.recurring || []

      // Set appointment requests.
      _appointments.pending =
        _appointmentRequests.filter(
          (x) =>
            x.type !== "DECLINED" &&
            x.type !== "BOOKED" &&
            x.type !== "CANCELLED" &&
            x.type !== "CANCELLED.DENIED",
        ) || []

      _appointments.declined =
        _appointmentRequests.filter(
          (appt) =>
            appt.type === "DECLINED" || appt.type === "CANCELLED.DENIED",
        ) || []

      setAppointments(_appointments)
      fetchScheduledAppointmentsToday(_recurringAppointments) // Update today's appointments count.
    } catch (error) {
      LogError("Error fetching data:", error)
      userCtx.setNotificationError(`Unable to load appointments.`)
    } finally {
      setLoadingAppointments(false)
      setLoadingAppointmentsSmall(false)
    }
  }

  async function fetchScheduledAppointmentsToday(recurringAppointments) {
    let _scheduledAppointments, _recurringAppointments
    const isTodayInRange = () => {
      const today = DateTime.local()
      const startDate = DateTime.fromJSDate(filterDateRange[0])
      const endDate = DateTime.fromJSDate(filterDateRange[1])
      return today >= startDate && today <= endDate
    }

    if (recurringAppointments && isTodayInRange()) {
      _recurringAppointments = recurringAppointments // Use the passed appointments if available.
    } else {
      // Fetch appointments for today.
      _scheduledAppointments = await fetchScheduledAppointments(true)
      _recurringAppointments = await fetchRecurringAppointments(
        _scheduledAppointments.scheduled_all,
      )
    }

    const _todayAppointmentsCount = _recurringAppointments.upcoming.filter(
      (x) => getDate(x.startDateTime).hasSame(DateTime.local(), "day"), // Filter for today's appointments.
    ).length

    setTodayAppointmentsCount(_todayAppointmentsCount)
  }

  async function fetchScheduledAppointments(todayOnly = false) {
    // Define the date range for fetching appointments.
    const fromDate = todayOnly
      ? DateTime.local()
      : DateTime.fromJSDate(filterDateRange[0])
    const toDate = todayOnly
      ? DateTime.local().plus({ days: 1 })
      : DateTime.fromJSDate(filterDateRange[1]).plus({
          days: 14, // Fetch 14 extra days to ensure comprehensive data for recurring event generation.
        })

    // Fetch all minified appointments.
    let res = await getAllMinifiedAppointments(
      userCtx.tokens.getAppointmentsToken(), // JWT token for authentication
      null, // Staff email, not used here
      fromDate.toFormat("yyyy-MM-dd"), // Start date in ISO format
      toDate.toFormat("yyyy-MM-dd"), // End date in ISO format
      userCtx.participant_alias, // Case ID
    )

    if (res.successful && res.data.length > 0) {
      // Process the appointments data.
      const _pastAppointments = res.data
        .map((x) => {
          x = formatDate(x) // Format the date.
          return x
        })
        .filter(
          (x) =>
            getDate(x.startDateTime).toUnixInteger() <
            DateTime.local().toUnixInteger(), // Filter for past appointments.
        )

      const returnValue = {
        past: _pastAppointments,
        scheduled_all: res.data, // will be passed on to the appointment generation function.
      }

      return returnValue
    } else if (!res.successful) {
      LogError("Unable to retrieve appointment data", res) // Log error if retrieval fails.
    }

    return {
      past: [],
      scheduled_all: [],
    } // Return an empty array if no appointments found or an error occurred.
  }

  async function fetchRecurringAppointments(upcoming) {
    // Define the date range for recurring appointments.
    var start = DateTime.fromJSDate(filterDateRange[0])

    let recurringAppts = [] // Array to hold recurring controllers.
    let generated = [] // Array to hold generated future recurring events.

    // Fetch recurring appointments (controllers).
    var res = await getAllMinifiedAppointments(
      userCtx.tokens.getAppointmentsToken(), // JWT token for authentication
      null, // Staff email - not used here
      null, // Start date in ISO format
      null, // End date in ISO format - not used here
      userCtx.participant_alias, // Case ID
      true, // Is recurring
      null, // Is pending - not used here
      null, // Organization ID - not used here
    )

    if (res.successful) {
      recurringAppts = res.data

      for (let controller of recurringAppts) {
        controller.isRecurringController = true // Mark as a recurring controller.

        controller = formatDate(controller) // Format the controller's dates.

        // Get localized frequency description.
        let localFrequency = getLocalControllerDescription(
          controller.frequency,
          controller.scheduledTime,
        )

        let frequency = controller.frequency.split(" | ") // Split frequency string.
        let newFreq // Variable for refined frequency display.

        // Adjust frequency display based on content.
        if (
          frequency[1] === "Daily" &&
          frequency[frequency.length - 1].includes(":")
        ) {
          newFreq = frequency[1]
        } else {
          newFreq = frequency[1] + " | " + frequency[frequency.length - 1]
        }

        controller.newFreq = newFreq // Set new frequency display.
        controller.frequencyPeriod = frequency[1] // Set frequency period (e.g., "Daily", "Weekly").
        controller.localFrequency = localFrequency // Set localized frequency.

        // Generate future recurring events based on the controller.
        const futureAppointments = generateFutureRecurringEventsForControllers(
          [controller],
          upcoming,
          filterDateRange[0],
          filterDateRange[1],
        )

        for (let futureDate of futureAppointments) {
          // Add generated events to the 'generated' array.
          generated.push({
            ...futureDate,
            isRecurringController: false, // Mark as a generated instance, not a controller.
            cron: null, // No cron for individual instances.
            id: `recurring_${start.toFormat("mmddyy")}_${controller.id}`, // Unique ID for generated instances.
          })
        }

        // Ensure unique ID for the controller.
        // Make sure to set this after futureAppointments have been generated
        controller.id = `recurring_${controller.id}`
      }
    } else {
      LogError("Unable to retrieve recurring appointments", res) // Log error if retrieval fails.
    }

    // Filter existing upcoming appointments to ensure they are actually in the future.
    const filteredUpcoming = upcoming
      .map((x) => {
        x = formatDate(x)
        return x
      })
      .filter(
        (x) =>
          getDate(x.startDateTime).toUnixInteger() >
          DateTime.local().toUnixInteger(), // Only include appointments in the future.
      )

    // Combine filtered existing upcoming appointments with newly generated recurring ones.
    const combinedUpcomingAppointments = [...filteredUpcoming, ...generated]

    const returnValue = {
      recurring: recurringAppts, // Return the recurring controllers.
      upcoming: combinedUpcomingAppointments, // Return the combined upcoming appointments.
    }

    return returnValue
  }

  async function fetchAppointmentRequests() {
    // Define a wider date range for pending appointments to capture older requests.
    var start = DateTime.fromJSDate(filterDateRange[0]).minus({ days: 120 })
    var end = DateTime.fromJSDate(filterDateRange[1]).plus({ days: 120 })

    // Fetch all minified appointments that are pending.
    let res = await getAllMinifiedAppointments(
      userCtx.tokens.getAppointmentsToken(), // JWT token for authentication
      null, // Staff email - not used here
      start.toFormat("yyyy-MM-dd"), // Start date in ISO format
      end.toFormat("yyyy-MM-dd"), // End date in ISO format
      userCtx.participant_alias, // Case ID
      false, // Not recurring
      true, // Only requests ("pending" appointments)
    )

    if (res.successful) {
      const _requests = res.data.map((x) => {
        x = formatDate(x) // Format the date.

        // Find the assigned staff for the appointment.
        const assignedStaff = userCtx.staff_assignment_list?.find(
          (staff) =>
            staff.staff_id === x.soarPeerId || staff.staff_id === x.peerId,
        )
        if (assignedStaff) {
          x.staff =
            assignedStaff.staff_name_first + " " + assignedStaff.staff_name_last // Add staff name.
        }

        return x
      })

      return _requests // Return the formatted requests.
    }
  }

  async function fetchStaffAvailabilities(assignedStaff) {
    if (!assignedStaff) {
      LogError("No staff provided for fetching availabilities.")
      return [] // Return empty array if no staff provided.
    }

    let res = await getAvailabilities(
      userCtx.tokens.getAppointmentsToken(),
      assignedStaff.staff_email_address, // Fetch availabilities for the assigned staff's email.
    )

    if (res.data && res.data.length > 0) {
      const filteredAvailabilities = res.data.filter((x) => {
        if (x.isRecurring) x.individualStartDate = x.recurringStartDate // For recurring, use recurringStartDate as individualStartDate for consistency.

        const currentLocalTime = DateTime.local()
        const individualEndDate = DateTime.fromJSDate(
          new Date(x.individualEndDate),
        )
        const recurringEndDate = DateTime.fromJSDate(
          new Date(x.recurringEndDate),
        )

        return (
          individualEndDate > currentLocalTime || // Filter out individual availabilities in the past.
          (x.isRecurring && recurringEndDate > currentLocalTime) // For recurring, check if recurringEndDate is in the future.
        )
      })

      return filteredAvailabilities // Return the filtered availabilities.
    }

    return [] // Return an empty array if no availabilities found.
  }

  function filterAppointments() {
    // Helper function to get a filtered list based on common criteria.
    const getFilteredList = (list, isRecurring, ignoreDateFilter) => {
      return applyAppointmentFilters(
        list,
        filterServices,
        services?.flat_unique || [],
        filterDateRange,
        ignoreDateFilter || isCalendarView, // Ignore date filter if in calendar view or explicitly set.
        isRecurring,
      )
    }

    // Apply filters to each cached list and update the respective state.
    const filteredUpcomingAppointments = getFilteredList(
      appointments?.upcoming || [],
    )
    const filteredPastAppointments = getFilteredList(appointments?.past || [])
    const filteredPendingRequests = getFilteredList(appointments?.pending || [])
    const filteredDeclinedRequests = getFilteredList(
      appointments?.declined || [],
    )
    const filteredRecurringControllers = getFilteredList(
      appointments?.recurring || [],
      true, // Mark as recurring for specific filter logic.
    ).filter((x) => {
      // Filter out recurring controllers that are not within the date range.
      const startDate = new Date(x.startDate)
      const endDate = new Date(x.endDate)
      return (
        (startDate.getTime() >= filterDateRange[0].getTime() &&
          startDate.getTime() <= filterDateRange[1].getTime()) ||
        (endDate.getTime() >= filterDateRange[0].getTime() &&
          endDate.getTime() <= filterDateRange[1].getTime()) ||
        (startDate.getTime() <= filterDateRange[0].getTime() &&
          endDate.getTime() >= filterDateRange[1].getTime())
      )
    })

    return {
      upcoming: filteredUpcomingAppointments,
      past: filteredPastAppointments,
      pending: filteredPendingRequests,
      declined: filteredDeclinedRequests,
      recurring: filteredRecurringControllers,
    }
  }

  function handleDateRangeChanged(dateRange) {
    // Update the date range for filtering appointments.
    setFilterDateRange(dateRange)

    // Reset the calendar selected date when the date range changes.
    setCalSelectedDate(null)
  }

  function openRequestModal() {
    // Open the appointment request modal in the specified mode.
    setRequestModalMode(requestModalModeOptions.request)
    setShowRequestAppt(true)
  }

  function openEditModal(appointment, mode) {
    // Set the appointment to edit and open the request modal.
    setAppointmentToEdit(appointment)
    setRequestModalMode(mode)
    setShowRequestAppt(true)
  }

  function openDeleteConfirmation(appointment) {
    confirmDialog({
      message: (
        <span>
          Are you sure you want to delete your{" "}
          <b>{appointment.engagementType}</b> request with{" "}
          <b>{appointment.staff}</b>?
        </span>
      ),
      header: "Delete Appointment",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        deleteRequest(appointment.id) // Call the delete function with the appointment ID.
      },
      acceptLabel: "Yes",
      rejectLabel: "No",
      acceptClassName: "p-button-danger", // Style the accept button as danger.
      rejectClassName: "p-button-secondary", // Style the reject button as secondary.
    })
  }

  function openCancelConfirmation(appointment) {
    confirmDialog({
      message: (
        <span>
          Are you sure you want to cancel your{" "}
          {appointment.isRecurringController && <u>{"recurring"}</u>}
          <b> {appointment.engagementType}</b> with <b>{appointment.staff}</b>?
        </span>
      ),
      header: "Request Appointment Cancellation",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setLoadingAppointmentsSmall(true)
        const payload = reformatFormikPayload(appointment, userCtx)
        const success = await submitAppointmentPayload(
          payload,
          requestModalModeOptions.cancel,
        ) // Submit the cancellation request.

        if (success) {
          fetchAppointments(true) // Refetch appointments after successful cancellation.
        } else {
          setLoadingAppointmentsSmall(false) // Just hide the loading state if submission failed.
        }
      },
      acceptLabel: "Yes",
      rejectLabel: "No",
      acceptClassName: "p-button-danger", // Style the accept button as danger.
      rejectClassName: "p-button-secondary", // Style the reject button as secondary.
    })
  }

  async function submitAppointmentPayload(payload, mode) {
    if (!payload) {
      LogError("No payload submitted for appointment request:", payload)
      return false // Return false if payload is invalid.
    }

    if (!mode) {
      LogError("No mode specified for appointment request submission.")
      return false // Return false if mode is not specified.
    }

    // Reformat the appointment request payload for the API.
    const staff = userCtx.staff_assignment_list?.find(
      (s) => s.staff_id === payload.staff,
    )
    const organization = userCtx.participant_organizations?.find(
      (o) => o.organization_id === payload.organization,
    )

    if (!staff || !organization) {
      LogError(
        "Could not find staff or organization for the appointment payload.",
        { payload },
      )
      userCtx.setNotificationError(
        "An error occurred. Could not find staff or organization.",
      )
      return false
    }

    // Reformat the appointment request payload for the API.
    const formattedPayload = reformatAppointmentRequest(
      payload,
      userCtx, // participant info
      staff, // staff info
      organization, // organization info
    )

    let successMessage = "Appointment request submitted successfully."
    let errorMessage = "Unable to submit appointment request."

    switch (mode) {
      case requestModalModeOptions.edit:
        successMessage = "Appointment request updated successfully."
        errorMessage = "Unable to update appointment request."
        break

      case requestModalModeOptions.cancel:
        successMessage =
          "Appointment cancellation request submitted successfully."
        errorMessage = "Unable to submit appointment cancellation request."
        formattedPayload.type = "CANCELLING"
        break

      case requestModalModeOptions.change:
        successMessage = "Appointment change request submitted successfully."
        errorMessage = "Unable to submit appointment change request."
        formattedPayload.type = "RESCHEDULE"
        formattedPayload.status = "ACTIVE" // Set status to ACTIVE (implies a pending change for an active appointment).

        if (
          formattedPayload.id.includes("recurring_") &&
          !formattedPayload.isRecurring
        ) {
          /* 
          If changing a single future instance of a recurring controller, override these values 
          to point to the original "appointment" start and end.

          Without this, the staff-client and API will have no way to determine the reschedule 
          request's original start and end.
          */

          formattedPayload.recurringStartDate = payload.originalStartDateTime
          formattedPayload.recurringEndDate = payload.originalEndDateTime
        }
        break
      default:
        formattedPayload.type = "NEW" // Default to "NEW" for any other mode.
        break
    }

    try {
      let res = null

      if (
        mode === requestModalModeOptions.change ||
        mode === requestModalModeOptions.cancel ||
        mode === requestModalModeOptions.edit
      ) {
        // Call the "edit" endpoint for existing appointments/changes.
        res = await editParticipantAppointment(
          userCtx.tokens.getAppointmentsToken(),
          formattedPayload,
        )
      } else {
        // Call the "send" endpoint for new appointment requests.
        res = await sendParticipantAppointment(
          userCtx.tokens.getAppointmentsToken(),
          formattedPayload,
        )
      }

      if (res?.successful) {
        toast.current.show({
          severity: "success",
          detail: successMessage,
          life: 3000,
        })

        fetchAppointments(true) // Refetch appointments after successful request
        return true // Return true if request was successful.
      } else {
        LogError(errorMessage, res?.error)
        userCtx.setNotificationError(res?.error)
      }
    } catch (error) {
      LogError(errorMessage, error)
      toast.current.show({
        severity: "error",
        detail: error.message,
        life: 9000,
      })
    }

    return false // Return false if request failed.
  }

  // Sends a request to decline/delete a participant appointment.
  async function deleteRequest(id) {
    setLoadingAppointmentsSmall(true)
    let res = await declineParticipantAppointment(
      userCtx.tokens.getAppointmentsToken(),
      id,
    ) // Call the API to decline/delete.

    if (res.successful) {
      toast.current.show({
        severity: "success",
        detail: "Appointment request deleted", // Success message.
        life: 3000,
      })

      fetchAppointments(true) // Refetch appointments after deletion.
    } else {
      userCtx.setNotificationError("Unable to delete appointment request") // Show error notification.
      LogError("Unable to delete appointment request", res) // Log error details.
    }
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* TopNav icon/button to open the main appointments dialog */}
      <li
        className="top-icon"
        onClick={() => {
          setShowApptsDialog(true)
        }}
      >
        <span className="material-icons p-overlay-badge">
          calendar_month
          {todayAppointmentsCount > 0 && (
            <Badge
              value={window.innerWidth > 1024 ? todayAppointmentsCount : null}
            />
          )}
        </span>
        <label className="top-menu-label">Appointments</label>
      </li>

      {/* Main appointments dialog */}
      {showApptsDialog && (
        <AppointmentsDialog
          fetchAppointments={fetchAppointments}
          showApptsDialog={showApptsDialog}
          setShowApptsDialog={setShowApptsDialog}
          initializationCompleted={initializationCompleted}
          isCalendarView={isCalendarView}
          setIsCalendarView={setIsCalendarView}
          filterServices={filterServices}
          setFilterServices={setFilterServices}
          services={services}
          loadingServices={loadingServices}
          loadingAppointments={loadingAppointments}
          loadingAppointmentsSmall={loadingAppointmentsSmall}
          calSelectedDate={calSelectedDate}
          setCalSelectedDate={setCalSelectedDate}
          handleDateRangeChanged={handleDateRangeChanged}
          filterDateRange={filterDateRange}
          filteredAppointments={filteredAppointments}
          openRequestModal={openRequestModal}
          openEditModal={openEditModal}
          openDeleteConfirmation={openDeleteConfirmation} // Pass the function to open delete confirmation.
          openCancelConfirmation={openCancelConfirmation} // Pass the function to open cancel confirmation.
        />
      )}

      {/* Appointment request/edit dialog */}
      {showRequestAppt && (
        <AppointmentsRequestDialog
          showRequestAppt={showRequestAppt}
          setShowRequestAppt={setShowRequestAppt}
          requestModalMode={requestModalMode}
          appointmentToEdit={appointmentToEdit}
          setAppointmentToEdit={setAppointmentToEdit} // Set the appointment being edited.
          fetchStaffAvailabilities={fetchStaffAvailabilities}
          services={services?.flat_unique || []}
          submitAppointmentPayload={submitAppointmentPayload}
        />
      )}
    </>
  )
}
